import { Component, inject, signal, OnInit, computed, DestroyRef, Output, EventEmitter, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { PromptBotService } from '../../../../core/services/prompt-bot.service';
import { Feature, ProductFeatureMapping, Product, TableActionEvent } from '../../../../core/models/prompt-bot.model';
import { ButtonComponent } from '../../../../shared/components/ui/button/button.component';
import { DropdownComponent, DropdownOption } from '../../../../shared/components/ui/dropdown/dropdown.component';
import { AlertComponent } from '../../../../shared/components/alert/alert.component';
import { PaginatedTableComponent, TableHeader } from '../../../../shared/components/tables/paginated-table/paginated-table.component';
import { ConfirmationModalComponent } from '../../../../shared/components/confirmation-modal/confirmation-modal.component';
import { PromptDetailModalComponent, PromptDetailData } from '../../../../shared/components/prompt-detail-modal/prompt-detail-modal.component';
import { IconName } from '../../../../shared/icons/icon.component';
import { AuditService } from '../../../../core/services/audit.service';
import { AuditAction, AuditEntityType } from '../../../../core/models/audit-log.model';

@Component({
  selector: 'app-prompt-mappings',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonComponent, DropdownComponent, AlertComponent, PaginatedTableComponent, MatDialogModule],
  templateUrl: './mappings.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./mappings.component.scss']
})
export class PromptMappingsComponent implements OnInit {
  private botService = inject(PromptBotService);
  private destroyRef = inject(DestroyRef);
  private dialog = inject(MatDialog);
  private auditService = inject(AuditService);

  @Output() actionChange = new EventEmitter<void>();

  products = signal<Product[]>([]);
  features = signal<Feature[]>([]);
  mappings = signal<ProductFeatureMapping[]>([]);
  selectedProductId = signal<number | null>(null);

  successMessage = signal('');

  newMapping: ProductFeatureMapping = { product_id: 1, feature_id: 0, is_active: true };

  String = String;

  productOptions = computed<DropdownOption[]>(() =>
    this.products().map(p => ({ value: String(p.id), label: p.name }))
  );

  featureOptions = computed<DropdownOption[]>(() =>
    this.features().map(f => ({ value: String(f.id!), label: f.name }))
  );

  headers: TableHeader[] = [
    { label: 'Feature Identity', key: 'featureName', align: 'text-left' },
    { label: 'System Key', key: 'featureKey', align: 'text-left' },
    { label: 'Description', key: 'description', align: 'text-left' },
    { label: 'Status', key: 'is_active', align: 'text-center' },
  ];

  actionOptions = [
    { label: 'View Details', value: 'view', iconName: 'eye' as IconName, isShow: true },
    { label: 'Sever Link', value: 'delete', iconName: 'trash' as IconName, isShow: true },
  ];

  ngOnInit() {
    this.loadProducts();
    this.loadFeatures();
  }

  loadProducts() {
    this.botService.getProducts()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: Product[]) => {
          this.products.set(data);
          // Small delay to ensure dropdown options are rendered before setting selection
          setTimeout(() => {
            this.selectedProductId.set(1);
            this.newMapping.product_id = 1;
            this.loadMappings(1);
          }, 100);
        }
      });
  }

  loadFeatures() {
    this.botService.getFeatures()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: Feature[]) => this.features.set(data)
      });
  }

  onProductChange(id: string | string[]) {
    const valStr = Array.isArray(id) ? id[0] : id;
    const val = Number(valStr);
    this.selectedProductId.set(val);
    this.loadMappings(val);
  }

  onFeatureSelection(id: string | string[]) {
    const valStr = Array.isArray(id) ? id[0] : id;
    this.newMapping.feature_id = Number(valStr);
  }

  loadMappings(productId: number, silent: boolean = false) {
    this.botService.getMappings(productId, { showLoader: !silent })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data: ProductFeatureMapping[]) => this.mappings.set(data)
      });
  }

  processedMappings = computed(() => {
    return this.mappings().map(m => {
      const feature = this.features().find(f => f.id === m.feature_id);
      const desc = feature?.description || 'No description available';
      return {
        ...m,
        featureName: feature?.name || 'Unknown',
        featureKey: feature?.keyname || 'N/A',
        description: desc.length > 100 ? desc.substring(0, 100) + '...' : desc
      };
    });
  });

  createMapping() {
    const mapping: ProductFeatureMapping = {
      product_id: this.selectedProductId()!,
      feature_id: this.newMapping.feature_id,
      is_active: true
    };

    this.botService.createMapping(mapping)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          const featureName = this.features().find(f => f.id === mapping.feature_id)?.name || mapping.feature_id;
          const selectedProduct = this.products().find(p => p.id === mapping.product_id);
          const productName = selectedProduct?.name || mapping.product_id;
          const productKey = selectedProduct?.keyname;

          this.auditService.createAuditLog({
            entityType: AuditEntityType.PROMPT_MAPPING,
            action: AuditAction.CREATE,
            actionTitle: `Prompt Mapping Created`,
            oldValue: null,
            newValue: {
              ...mapping,
              id: res.id,
              featureName,
              productName
            },
            productKey,
            note: `Linked feature '${featureName}' to product '${productName}'`
          }).subscribe({
            next: () => {
              this.newMapping.feature_id = 0;
              const productId = this.selectedProductId();
              if (productId) this.loadMappings(productId, true);
              this.actionChange.emit();
            },
            error: () => {
              this.newMapping.feature_id = 0;
              const productId = this.selectedProductId();
              if (productId) this.loadMappings(productId, true);
              this.actionChange.emit();
            }
          });
        }
      });
  }

  handleAction(event: TableActionEvent<ProductFeatureMapping>) {
    if (event.action === 'view') {
      this.openViewModal(event.row);
    } else if (event.action === 'delete') {
      const dialogRef = this.dialog.open(ConfirmationModalComponent, {
        width: '400px',
        data: {
          title: 'Confirm Decoupling',
          description: 'Are you sure you want to decouple this feature capability? Active prompts will cease function.'
        },
        panelClass: 'custom-dialog-container'
      });

      dialogRef.afterClosed().subscribe((result) => {
        if (result) {
          this.botService.deleteMapping(event.row.id!)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: () => {
                const featureName = this.features().find(f => f.id === event.row.feature_id)?.name || event.row.feature_id;
                const deletedProduct = this.products().find(p => p.id === event.row.product_id);
                const productName = deletedProduct?.name || event.row.product_id;
                const productKey = deletedProduct?.keyname;

                this.auditService.createAuditLog({
                  entityType: AuditEntityType.PROMPT_MAPPING,
                  action: AuditAction.DELETE,
                  actionTitle: `Prompt Mapping Deleted`,
                  oldValue: {
                    id: event.row.id,
                    product: (event.row as any).product ? {
                      id: (event.row as any).product.id,
                      name: (event.row as any).product.name,
                    } : undefined,
                    feature: event.row.feature ? {
                      id: event.row.feature.id,
                      name: event.row.feature.name,
                    } : undefined,
                    featureName: (event.row as any).featureName,
                    featureKey: (event.row as any).featureKey,
                    description: (event.row as any).description
                  },
                  newValue: null,
                  productKey,
                  note: `Prompt Mapping decoupled feature '${featureName}' from product '${productName}'`
                }).subscribe({
                  next: () => {
                    const productId = this.selectedProductId();
                    if (productId) this.loadMappings(productId, true);
                    this.actionChange.emit();
                  },
                  error: () => {
                    const productId = this.selectedProductId();
                    if (productId) this.loadMappings(productId, true);
                    this.actionChange.emit();
                  }
                });
              }
            });
        }
      });
    }
  }

  openViewModal(mapping: ProductFeatureMapping) {
    const feature = this.features().find(f => f.id === mapping.feature_id);
    const product = this.products().find(p => p.id === mapping.product_id)?.name || 'Unknown';

    const modalData: PromptDetailData = {
      title: 'Capability Mapping Link Details',
      fields: [
        { label: 'Target Product', value: product },
        { label: 'Linked Feature', value: feature?.name || 'N/A' },
        { label: 'System Key', value: feature?.keyname || 'N/A' },
        { label: 'Link Status', value: mapping.is_active ? 'Active' : 'Archived' }
      ],
      highlightLabel: 'Feature Scope / Description',
      highlightValue: feature?.description || 'No detailed documentation available for this feature capability.'
    };

    this.dialog.open(PromptDetailModalComponent, {
      data: modalData,
      width: '600px',
      panelClass: 'custom-modal-panel'
    });
  }
}
